import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const stepKeys = [
  { number: '01', icon: 'ri-search-2-line', titleKey: 'process.s1.title', descKey: 'process.s1.desc', detailKey: 'process.s1.detail' },
  { number: '02', icon: 'ri-shield-check-line', titleKey: 'process.s2.title', descKey: 'process.s2.desc', detailKey: 'process.s2.detail' },
  { number: '03', icon: 'ri-settings-3-line', titleKey: 'process.s3.title', descKey: 'process.s3.desc', detailKey: 'process.s3.detail' },
  { number: '04', icon: 'ri-key-2-line', titleKey: 'process.s4.title', descKey: 'process.s4.desc', detailKey: 'process.s4.detail' },
];

export default function ImportProcess() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="importacion" ref={sectionRef} className="py-16 sm:py-28 bg-[#050505] relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full bg-purple-900/8 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-20 scroll-reveal">
          <div className="inline-flex items-center gap-2 border border-purple-500/30 px-4 py-2 rounded-full mb-6">
            <span className="text-purple-400 text-xs tracking-widest uppercase font-medium">{t('process.label')}</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-4" style={{ letterSpacing: '0.03em' }}>
            {t('process.title1')}{' '}
            <span className="gradient-text-purple">{t('process.title2')}</span>
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto">
            {t('process.subtitle')}
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-purple-500/40 to-transparent -translate-x-1/2" />

          <div className="flex flex-col gap-8 sm:gap-12 lg:gap-0">
            {stepKeys.map((step, i) => (
              <div
                key={step.number}
                className={`scroll-reveal relative flex flex-col lg:flex-row items-center gap-8 lg:gap-0 ${
                  i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                {/* Content */}
                <div className={`w-full lg:w-5/12 ${i % 2 === 0 ? 'lg:pr-16 lg:text-right' : 'lg:pl-16 lg:text-left'}`}>
                  <div className="glass-card glass-card-hover rounded-2xl p-8 transition-all duration-400 group">
                    <div className={`flex items-center gap-4 mb-4 ${i % 2 === 0 ? 'lg:flex-row-reverse' : ''}`}>
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-purple-900/30 border border-purple-500/20 group-hover:bg-purple-600/30 transition-all duration-300 flex-shrink-0">
                        <i className={`${step.icon} text-purple-400 text-xl`} />
                      </div>
                      <span className="font-display text-5xl text-purple-500/20 group-hover:text-purple-500/40 transition-colors duration-300" style={{ letterSpacing: '0.02em' }}>
                        {step.number}
                      </span>
                    </div>
                    <h3 className="font-heading text-2xl text-white mb-3" style={{ letterSpacing: '0.04em' }}>
                      {t(step.titleKey)}
                    </h3>
                    <p className="text-white/50 text-sm leading-relaxed mb-4">
                      {t(step.descKey)}
                    </p>
                    <div className="flex items-center gap-2 text-purple-400/70">
                      <i className="ri-check-line text-sm" />
                      <span className="text-xs tracking-wide">{t(step.detailKey)}</span>
                    </div>
                  </div>
                </div>

                {/* Center dot */}
                <div className="hidden lg:flex w-2/12 justify-center">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full gradient-purple flex items-center justify-center glow-purple z-10 relative">
                      <span className="font-label text-white text-xs">{step.number}</span>
                    </div>
                  </div>
                </div>

                {/* Empty space */}
                <div className="hidden lg:block w-5/12" />
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-20 scroll-reveal">
          <button
            onClick={() => {
              const el = document.querySelector('#contacto');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="gradient-purple text-white font-semibold tracking-wider uppercase px-10 py-4 rounded-full glow-purple hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-rocket-line mr-2" />
            {t('process.cta')}
          </button>
        </div>
      </div>
    </section>
  );
}